import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { SessionOrganization, SessionUser } from '../types';

type UsePendoLifecycleOptions = {
  currentUser: SessionUser | null;
  isAuthenticated: boolean;
  org: SessionOrganization | null;
};

function getPendo() {
  if (typeof window === 'undefined') return null;
  return window.pendo || null;
}

export default function usePendoLifecycle({
  currentUser,
  isAuthenticated,
  org,
}: UsePendoLifecycleOptions) {
  const location = useLocation();

  useEffect(() => {
    const pendo = getPendo();
    if (!isAuthenticated || !currentUser?.id || !org?.id || !pendo) return;

    try {
      pendo.initialize({
        visitor: {
          id: String(currentUser.id),
          email: currentUser.email ? String(currentUser.email) : undefined,
          full_name: currentUser.name ? String(currentUser.name) : undefined,
          role: currentUser.role ? String(currentUser.role) : undefined,
        },
        account: {
          id: String(org.id),
          name: org.name ? String(org.name) : undefined,
          market: org.market ? String(org.market) : undefined,
          facilities_count: org.facilities != null ? Number(org.facilities) : undefined,
          users_count: org.users != null ? Number(org.users) : undefined,
        },
      });
      pendo.startGuides();
    } catch (error) {
      console.error('Error initializing Pendo:', error);
    }
  }, [currentUser, isAuthenticated, org]);

  useEffect(() => {
    const pendo = getPendo();
    if (!pendo) return;

    try {
      pendo.pageLoad({
        url: window.location.href,
      });
    } catch (error) {
      console.error('Error tracking Pendo page load:', error);
    }
  }, [location.pathname, location.search]);

  const stopGuides = useCallback(() => {
    const pendo = getPendo();
    if (!pendo) return;

    try {
      pendo.stopGuides();
    } catch (error) {
      console.error('Error stopping Pendo:', error);
    }
  }, []);

  return { stopGuides };
}
